import { MdOpenInNew, MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { truncateText } from '@/utils/truncateText';
import { Button } from '../../../../components/Button';
import { Tag, type TagColor } from '../../../../components/Tag';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import type { AgentApiProvidedJobPostsRecord } from '@/types/serializers';
import JobPostStatusBadge from '../../provided_job_posts/components/JobPostStatusBadge';
import Checkbox from '../../../../components/Checkbox';

type Props = {
  providedJobPost: AgentApiProvidedJobPostsRecord;
  isSelected?: boolean;
  onSelectionChange?: (selected: boolean) => void;
  topContent?: ReactNode;
  bottomContent?: ReactNode;
};

export default function CandidatesProvidedJobPostCard({
  providedJobPost,
  isSelected = false,
  onSelectionChange,
  topContent,
  bottomContent,
}: Props) {
  const [showRequirements, setShowRequirements] = useState(false);
  const [showRequirementsShowButton, setShowRequirementsShowButton] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);
  const requirementsRef = useRef<HTMLParagraphElement>(null);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const requirements = providedJobPost.mandatory_requirement;

  useEffect(() => {
    if (requirementsRef.current) {
      // スクロール可能な高さと実際の高さを比較して省略されているかを判定
      const isOverflowing =
        requirementsRef.current.scrollHeight > requirementsRef.current.clientHeight;
      setShowRequirementsShowButton(isOverflowing);
    }
  }, [requirements]);

  // NOTE: タイトルが省略されているかを判定
  useEffect(() => {
    if (titleRef.current) {
      const isTruncated = titleRef.current.scrollWidth > titleRef.current.clientWidth;
      setIsTitleTruncated(isTruncated);
    }
  }, [providedJobPost.title]);

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (
      target.closest('input[type="checkbox"]') ||
      target.closest('button') ||
      target.closest('a')
    ) {
      return;
    }

    if (onSelectionChange) {
      onSelectionChange(!isSelected);
    }
  };

  return (
    <div
      className={`flex flex-col gap-3 rounded bg-white px-lg py-md shadow-layer1 transition-shadow ${
        onSelectionChange ? 'cursor-pointer hover:bg-gray' : ''
      }`}
      onClick={handleCardClick}
    >
      {topContent}

      <div className="flex items-center justify-between">
        <div className="flex min-w-0 flex-1 items-center gap-4">
          {onSelectionChange && (
            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox checked={isSelected} onChange={onSelectionChange} />
            </div>
          )}
          <div className="relative min-w-0 flex-1">
            <p
              ref={titleRef}
              className="truncate text-xl leading-[150%] font-bold"
              onMouseEnter={() => isTitleTruncated && setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              {providedJobPost.title}
            </p>
            {showTooltip && isTitleTruncated && (
              <div className="absolute top-full left-1/2 z-10 mt-xs max-w-[540px] -translate-x-1/2 rounded-xs bg-black p-xs shadow-lg">
                <div
                  className="absolute top-[-6px] left-1/2 h-2 w-3 -translate-x-1/2 bg-black"
                  style={{ clipPath: 'polygon(45.45% 0%, 0% 100%, 100% 100%)' }}
                ></div>
                <p className="text-xs leading-[170%] break-words text-white">
                  {providedJobPost.title}
                </p>
              </div>
            )}
          </div>
          <div className="mr-2 shrink-0">
            <JobPostStatusBadge
              id={providedJobPost.id}
              status={providedJobPost.status}
              created_at={providedJobPost.created_at}
            />
          </div>
        </div>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {/* 取り込み元ページへのリンク */}
          {providedJobPost.outer_job_post && (
            <Button color="tertiary" size="sm" asChild>
              <a
                href={providedJobPost.outer_job_post.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1"
              >
                {providedJobPost.outer_job_post.job_source_display_name ||
                  providedJobPost.outer_job_post.job_source_name ||
                  '取り込み元を見る'}
                <MdOpenInNew className="size-icon-xxs" />
              </a>
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-start justify-between gap-1">
        <div className="flex flex-wrap gap-4">
          {providedJobPost.company && (
            <div className="flex items-center gap-2 border-r border-solid border-gray-300 pr-4">
              <p className="text-xs font-bold">企業名</p>
              <p className="text-xs">{providedJobPost.company.name}</p>
            </div>
          )}
          {providedJobPost.job_position_name && (
            <div className="flex min-w-0 items-center gap-2 border-r border-solid border-gray-300 pr-4">
              <p className="shrink-0 text-xs font-bold">ポジション</p>
              <p className="min-w-0 truncate text-xs" title={providedJobPost.job_position_name}>
                {truncateText(providedJobPost.job_position_name)}
              </p>
            </div>
          )}
          {providedJobPost.salary_info && (
            <div className="flex items-center gap-2 border-r border-solid border-gray-300 pr-4">
              <p className="text-xs font-bold">年収</p>
              <p className="text-xs">{providedJobPost.salary_info}</p>
            </div>
          )}
          {providedJobPost.working_location && (
            <div className="flex items-center gap-2">
              <p className="text-xs font-bold">勤務地</p>
              <p className="text-xs">{providedJobPost.working_location}</p>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-1">
        <div className="flex flex-wrap items-center gap-2">
          {providedJobPost.tags.length !== 0 &&
            providedJobPost.tags.map((tag) => (
              <Tag key={tag.id} color={tag.color_hex as TagColor} size="sm">
                {tag.name}
              </Tag>
            ))}
        </div>
      </div>

      {requirements && (
        <div className="rounded border border-solid border-border bg-white p-4">
          <div className="flex items-start gap-md">
            <p className="text-heading-xxs font-bold whitespace-nowrap">必須要件</p>
            <div className="flex flex-col gap-2">
              <p
                ref={requirementsRef}
                className={`text-body-xs break-words whitespace-pre-wrap ${
                  !showRequirements ? 'line-clamp-3' : ''
                }`}
              >
                {requirements}
              </p>
              {showRequirementsShowButton && (
                <div className="w-fit">
                  <Button
                    color="tertiary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setShowRequirements(!showRequirements);
                    }}
                  >
                    {showRequirements ? (
                      <>
                        <MdKeyboardArrowUp className="size-icon-xxs" />
                        閉じる
                      </>
                    ) : (
                      <>
                        <MdKeyboardArrowDown className="size-icon-xxs" />
                        要件をもっと見る
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {bottomContent}
    </div>
  );
}
